import { useEffect } from "react";
import { Box, IconButton, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";
import {
  SettingsIcon,
  SmileIcon,
  TabletIcon,
  UserRoundIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import {
  DevicePermissionEnum,
  SubjectPermissionEnum,
  UserPermissionEnum,
} from "authorization/types";
import { TabButton } from "components/TabButton/TabButton";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useNavigate } from "hooks/useNavigate";
import { IZone } from "interfaces/zone.interface";

import { DeviceEditPage } from "./devices/DeviceEditPage";
import { DeviceListPage } from "./devices/DeviceListPage";
import { SubjectCreatePage } from "./subjects/SubjectCreatePage";
import { SubjectEditPage } from "./subjects/SubjectEditPage";
import { SubjectListPage } from "./subjects/SubjectListPage";
import { UserCreatePage } from "./users/UserCreatePage";
import { UserEditPage } from "./users/UserEditPage";
import { UserListPage } from "./users/UserListPage";
import { ZoneEditPage } from "./zoneSettings/ZoneEditPage";

import { useAdministrationStore } from "../administrationStore";
import { ZoneDrawer } from "../components/ZoneDrawer";

const DynamicTabButton = styled(TabButton)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    ".MuiButton-icon": {
      display: "none",
    },
  },
})) as typeof TabButton;

const Header = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  borderWidth: 0,
  borderBottomWidth: 1,
  borderStyle: "solid",
  borderColor: theme.vars.palette.backgrounds.neutral.strong.default,
}));

type ZoneHeaderProps = {
  zones: IZone[];
};

function ZoneHeader({ zones }: ZoneHeaderProps) {
  const { t } = useTranslation();

  const { zoneId } = useParams();
  const location = useLocation();

  const { permissions } = useAuthenticatedUser();

  return (
    <Header>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          paddingTop: { xs: 1, md: 2.5 },
          paddingInline: { xs: 1, md: 6 },
        }}
      >
        <DynamicTabButton
          component={Link}
          to={`/administration/zones/${zoneId}/devices`}
          startIcon={<TabletIcon />}
          disabled={!permissions.includes(DevicePermissionEnum["device.list"])}
          aria-current={location.pathname.includes("/devices")}
        >
          {t("Devices")}
        </DynamicTabButton>
        <DynamicTabButton
          component={Link}
          to={`/administration/zones/${zoneId}/subjects`}
          startIcon={<SmileIcon />}
          disabled={
            !permissions.includes(SubjectPermissionEnum["subject.list"])
          }
          aria-current={location.pathname.includes("/subjects")}
        >
          {t("Subjects")}
        </DynamicTabButton>
        <DynamicTabButton
          component={Link}
          to={`/administration/zones/${zoneId}/users`}
          startIcon={<UserRoundIcon />}
          disabled={!permissions.includes(UserPermissionEnum["user.list"])}
          aria-current={location.pathname.includes("/users")}
        >
          {t("Employees")}
        </DynamicTabButton>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <ZoneDrawer zones={zones} />
      <Box sx={{ paddingTop: 2, paddingRight: 3 }}>
        <IconButton
          component={Link}
          to={`/administration/zones/${zoneId}/settings`}
        >
          <SettingsIcon />
        </IconButton>
      </Box>
    </Header>
  );
}

type ZoneLandingPageProps = {
  zones: IZone[];
};

export const ZoneLandingPage = ({ zones }: ZoneLandingPageProps) => {
  const { zoneId } = useParams();
  const navigate = useNavigate();

  const selectedZoneId = useAdministrationStore((s) => s.selectedZoneId);
  const setSelectedZoneId = useAdministrationStore((s) => s.setSelectedZoneId);

  const zone = zones.find((zone) => zone.id === (zoneId ?? selectedZoneId));

  useEffect(() => {
    if (zoneId) {
      setSelectedZoneId(zoneId);
    } else {
      const newId = selectedZoneId ?? zones.find((z) => z.hasAccess)?.id;

      if (newId) {
        navigate(`/administration/zones/${newId}/devices`, { replace: true });
      }
    }
  }, [navigate, setSelectedZoneId, selectedZoneId, zoneId, zones]);

  if (!zones.length) {
    return <Typography>{"No zones"}</Typography>;
  }

  if (!zone) {
    return <Typography>{"Not found"}</Typography>;
  }

  return (
    <Box sx={{ maxWidth: "1200px" }}>
      <ZoneHeader zones={zones} />
      <Box sx={{ padding: 2 }}>
        <Routes>
          <Route index element={<Navigate to="devices" replace />} />
          <Route
            path="devices"
            element={<DeviceListPage zone={zone} zones={zones} />}
          />
          <Route path="devices/:id" element={<DeviceEditPage />} />

          <Route
            path="subjects"
            element={<SubjectListPage zone={zone} zones={zones} />}
          />
          <Route
            path="subjects/create"
            element={<SubjectCreatePage zones={zones} />}
          />
          <Route
            path="subjects/:subjectId"
            element={<SubjectEditPage zones={zones} />}
          />
          <Route
            path="users"
            element={<UserListPage zone={zone} zones={zones} />}
          />
          <Route
            path="users/create"
            element={<UserCreatePage zones={zones} />}
          />
          <Route
            path="users/:userId"
            element={<UserEditPage zones={zones} />}
          />
          <Route
            path={"settings"}
            element={<ZoneEditPage zone={zone} zones={zones} />}
          />
        </Routes>
      </Box>
    </Box>
  );
};
